import React, { useCallback, useEffect, useState } from 'react'
import { Box, CircularProgress, useTheme } from '@mui/material'

import { useApp } from '../../AppContext/AppProvider'
import {
  postBranchLogEvent,
  BRANCH_LOG_EVENTS
} from '../../../services/branch.service'
import {
  declineApplicationCreditOffer,
  getApplicationDoc,
  getApplicationStatus
} from '../../../services/creditApplication.service'
import {
  ApplicationDoc,
  CreditApplicationDetails,
  GENERIC
} from '../../../domain/creditApplication'

import { ApplicationReadyComponent } from './ApplicationReady.component'
import {
  AcceptButtonEvent,
  ApplicationReadyProps,
  DeclineOfferButtonEvent,
  PreApprovedScreenEvent,
  RISK_BASED_PRICING_NOTICE
} from './types'
import { logger } from '../../../services/logger'
import { acceptCreditOffer } from '../../../services/creditCard.service'
import { ApplicationOfferDeclineContainer } from '../ApplicationOfferDeclined/ApplicationOfferDeclined.container'
import {
  sendTrackingEvent,
  toButtonEvent,
  toScreenEvent
} from '../../../services/amplitude.service'
import { useConfiguration } from '../../AppConfigProvider/AppConfigProvider'
import { useFetch } from '../../../hooks/fetch.hook'

/**
 *
 */
export const ApplicationReadyContainer: React.FC<ApplicationReadyProps> = ({
  applicationId,
  setCardSubmitted,
  handleError
}) => {
  const theme = useTheme()
  const { application } = useApp()
  const { getOverrideByPromoCode } = useConfiguration()
  const [riskBasedPricingData, setRiskBasedPricingData] =
    useState<ApplicationDoc | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [offerDeclined, setOfferDeclined] = useState<boolean>(false)
  const [decliningOffer, setDeclining] = useState<boolean>(false)
  const acceptCredit = acceptCreditOffer(application)
  const declineApplication = declineApplicationCreditOffer(application)
  const getApplicationDetails = getApplicationStatus(application)
  const { data: userApplication } = useFetch<CreditApplicationDetails, string>(
    getApplicationDetails,
    applicationId
  )

  const { apr, limit, documents, id, promotionCode } = userApplication || {}

  const creditApplicationId = id || ''

  const riskBasedPricingNotice = documents?.filter(
    (doc) => doc.type === RISK_BASED_PRICING_NOTICE
  )[0]
  const shouldGetRiskBasedPricingDoc =
    riskBasedPricingNotice && riskBasedPricingData === null

  /**
   * Use the applied with promotion code
   */
  const promotionOverrides = getOverrideByPromoCode(promotionCode || '')

  /**
   * Helps with unnecesary renders
   */
  const handleDeclineOffer = useCallback(() => {
    sendTrackingEvent(toButtonEvent({ ...DeclineOfferButtonEvent }))
    setDeclining(true)
    declineApplication(creditApplicationId)
      .then(() => setOfferDeclined(true))
      .finally(() => setDeclining(false))
  }, [])

  const handleAcceptOffer = (): Promise<void> => {
    sendTrackingEvent(toButtonEvent({ ...AcceptButtonEvent }))
    setSubmitting(true)
    return acceptCredit(creditApplicationId)
      .then(setCardSubmitted)
      .then(() => postBranchLogEvent(BRANCH_LOG_EVENTS.APP_ACCEPTED))
      .catch(() => {
        handleError(GENERIC)
      })
      .finally(() => setSubmitting(false))
  }

  const getRiskBasedPricingNotice = (docId: string): Promise<void> => {
    const getDocById = getApplicationDoc(application)

    return getDocById(creditApplicationId, docId)
      .then(setRiskBasedPricingData)
      .catch((e: any) => {
        // Do we need error handling for this?
        if (e.statusCode === 400) {
          logger.info('Document not found')
        }
      })
  }

  useEffect(() => {
    if (shouldGetRiskBasedPricingDoc) {
      getRiskBasedPricingNotice(riskBasedPricingNotice.id)
    }
  }, [shouldGetRiskBasedPricingDoc])

  useEffect(() => {
    if (userApplication) {
      const screenEvent = userApplication.promotionCode
        ? { ...PreApprovedScreenEvent, asset_id: userApplication.promotionCode }
        : PreApprovedScreenEvent
      sendTrackingEvent(toScreenEvent({ ...screenEvent }))
    }
  }, [userApplication])

  if (offerDeclined) {
    return <ApplicationOfferDeclineContainer />
  }

  const creditOffer = limit && apr ? { limit, ...apr } : undefined

  return (
    <>
      {shouldGetRiskBasedPricingDoc ? (
        <Box
          sx={{
            textAlign: 'center',
            mt: theme.spacing(2)
          }}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <ApplicationReadyComponent
          creditOffer={creditOffer}
          riskBasedPricingDoc={riskBasedPricingData?.fileBase64String}
          onDeclineOffer={handleDeclineOffer}
          onAcceptOffer={handleAcceptOffer}
          loading={submitting}
          decliningOffer={decliningOffer}
          promotionOverride={promotionOverrides}
        />
      )}
    </>
  )
}
